import React, { useEffect, useState } from 'react';
import Sidebar from '../components/layout/sidebar';
import Topbar from '../components/layout/Topbar';
import { supabase } from '../supabase';
import './navigationMenu.css';

const emptyForm = {
  label_en: '',
  label_ar: '',
  link: '',
  sort_order: 1,
  is_visible: true,
};

const NavigationMenu = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('isAdminLoggedIn') !== 'true') {
      window.location.href = '/';
      return;
    }

    fetchMenuItems();
  }, []);

  const fetchMenuItems = async () => {
    const { data, error } = await supabase
      .from('nav_menu')
      .select('*')
      .order('sort_order', { ascending: true });

    if (error) {
      alert(error.message);
      return;
    }

    setMenuItems(data || []);
  };

  const openAddModal = () => {
    setForm({
      ...emptyForm,
      sort_order: menuItems.length + 1,
    });
    setEditingId(null);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setForm({
      label_en: item.label_en || '',
      label_ar: item.label_ar || '',
      link: item.link || '',
      sort_order: item.sort_order || 1,
      is_visible: item.is_visible,
    });

    setEditingId(item.id);
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm({
      ...form,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      label_en: form.label_en,
      label_ar: form.label_ar,
      link: form.link,
      sort_order: Number(form.sort_order),
      is_visible: form.is_visible,
    };

    let result;

    if (editingId) {
      result = await supabase.from('nav_menu').update(payload).eq('id', editingId);
    } else {
      result = await supabase.from('nav_menu').insert([payload]);
    }

    if (result.error) {
      alert(result.error.message);
      return;
    }

    setShowModal(false);
    setEditingId(null);
    setForm(emptyForm);
    fetchMenuItems();
  };

  const deleteMenuItem = async (id) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this menu item?');

    if (!confirmDelete) return;

    const { error } = await supabase.from('nav_menu').delete().eq('id', id);

    if (error) {
      alert(error.message);
      return;
    }

    fetchMenuItems();
  };

  const toggleVisibility = async (item) => {
    const { error } = await supabase
      .from('nav_menu')
      .update({ is_visible: !item.is_visible })
      .eq('id', item.id);

    if (error) {
      alert(error.message);
      return;
    }

    fetchMenuItems();
  };

  const moveItem = async (item, direction) => {
    const sorted = [...menuItems].sort((a, b) => a.sort_order - b.sort_order);
    const index = sorted.findIndex((menuItem) => menuItem.id === item.id);
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= sorted.length) return;

    const target = sorted[targetIndex];

    await supabase
      .from('nav_menu')
      .update({ sort_order: target.sort_order })
      .eq('id', item.id);

    await supabase
      .from('nav_menu')
      .update({ sort_order: item.sort_order })
      .eq('id', target.id);

    fetchMenuItems();
  };

  const visibleItems = menuItems.filter((item) => item.is_visible);

  return (
    <div className="dashboard-page">
      <Sidebar active="Navigation Menu" />

      <main className="dashboard-main">
        <Topbar />

        <section className="navigation-content">
          <div className="navigation-header">
            <div>
              <h1>Navigation Menu</h1>
              <p>Manage navigation menu items and order</p>
            </div>

            <button className="add-menu-btn" onClick={openAddModal}>
              + Add Menu Item
            </button>
          </div>

          <div className="nav-table-card">
            <div className="nav-table-wrap">
              <table className="nav-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Label (English)</th>
                    <th>Label (Arabic)</th>
                    <th>Link</th>
                    <th>Visibility</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {menuItems.map((item, index) => (
                    <tr key={item.id}>
                      <td>
                        <div className="order-cell">
                          <span>{item.sort_order}</span>
                          <div>
                            <button onClick={() => moveItem(item, 'up')}>↑</button>
                            <button onClick={() => moveItem(item, 'down')}>↓</button>
                          </div>
                        </div>
                      </td>

                      <td>{item.label_en}</td>
                      <td className="arabic-label">{item.label_ar}</td>
                      <td className="nav-link">{item.link}</td>

                      <td>
                        <button
                          className={`visibility-pill ${item.is_visible ? 'visible' : 'hidden'}`}
                          onClick={() => toggleVisibility(item)}
                        >
                          {item.is_visible ? 'Visible' : 'Hidden'}
                        </button>
                      </td>

                      <td>
                        <div className="nav-actions">
                          <button onClick={() => openEditModal(item)}>✎</button>
                          <button onClick={() => deleteMenuItem(item.id)}>🗑</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {menuItems.length === 0 && (
                <div className="empty-nav">No menu items found.</div>
              )}
            </div>
          </div>

          <div className="nav-preview-card">
            <h2>Navigation Preview</h2>

            <div className="nav-preview-bar">
              {visibleItems.map((item) => (
                <span key={item.id}>{item.label_en}</span>
              ))}
            </div>

            <div className="nav-preview-bar arabic">
              {visibleItems.map((item) => (
                <span key={item.id}>{item.label_ar}</span>
              ))}
            </div>
          </div>
        </section>

        <footer className="dashboard-footer">
          <p>© 2026 Spine Literary Events Platform. All rights reserved.</p>
          <p>Admin Dashboard v1.0</p>
        </footer>
      </main>

      {showModal && (
        <div className="nav-modal-overlay">
          <div className="nav-modal">
            <div className="nav-modal-header">
              <h2>{editingId ? 'Edit Menu Item' : 'Add Menu Item'}</h2>
              <button onClick={() => setShowModal(false)}>×</button>
            </div>

            <form className="nav-form" onSubmit={handleSubmit}>
              <input
                name="label_en"
                placeholder="Label English"
                value={form.label_en}
                onChange={handleChange}
                required
              />

              <input
                name="label_ar"
                placeholder="Label Arabic"
                value={form.label_ar}
                onChange={handleChange}
                required
              />

              <input
                name="link"
                placeholder="Link e.g. /events or #events"
                value={form.link}
                onChange={handleChange}
                required
              />

              <input
                name="sort_order"
                type="number"
                min="1"
                placeholder="Order"
                value={form.sort_order}
                onChange={handleChange}
                required
              />

              <label className="visible-checkbox">
                <input
                  type="checkbox"
                  name="is_visible"
                  checked={form.is_visible}
                  onChange={handleChange}
                />
                Visible in navigation
              </label>

              <div className="nav-modal-actions">
                <button type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit">
                  {editingId ? 'Save Changes' : 'Create Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NavigationMenu;